/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import type { SVGProps } from 'react'
import { seriesStyle } from 'lib/chartColors'

/** Chip corner radius (`rounded`). */
const CAP_RADIUS = 4
/** Half a pixel keeps the hairline on whole pixels and inside the band, like a chip's border. */
const INSET = 0.5

/** Left open, the fill still closes but the bottom edge goes unstroked, so the segment below draws the only seam line. */
function chipPath(x: number, y: number, w: number, h: number, r: number, closed: boolean) {
  const outline =
    r > 0
      ? [`v${-(h - r)}`, `q0,${-r} ${r},${-r}`, `h${w - 2 * r}`, `q${r},0 ${r},${r}`, `v${h - r}`]
      : [`v${-h}`, `h${w}`, `v${h}`]
  return [`M${x},${y + h}`, ...outline, ...(closed ? [`Z`] : [])].join(` `)
}

type ChipSegmentProps = {
  x: number
  y: number
  width: number
  height: number
  color: string
  rounded: boolean
  base: boolean
} & Pick<SVGProps<SVGPathElement>, `onClick` | `onMouseMove` | `onMouseLeave`>

/** One stacked-bar segment drawn as a chip. Empty segments draw nothing, or their outline would leave a stray hairline. */
export function ChipSegment({ x, y, width, height, color, rounded, base, ...handlers }: ChipSegmentProps) {
  if (height <= 0) return null
  const colors = seriesStyle(color)
  const w = Math.max(0, width - 2 * INSET)
  const h = Math.max(0, height - (base ? 2 : 1) * INSET)
  const r = rounded ? Math.min(CAP_RADIUS, w / 2, h) : 0
  return (
    <path
      d={chipPath(x + INSET, y + INSET, w, h, r, base)}
      // `style`, not `fill`/`stroke` attributes: the fill can be a `color-mix(..., var(--surface))`
      // string, which only resolves reliably as CSS, not as an SVG presentation attribute.
      style={{ fill: colors.fill, stroke: colors.stroke }}
      strokeWidth={1}
      {...handlers}
    />
  )
}
